// src/contexts/AuthContext.tsx

import { unregisterNativePush } from 'platform/push';
import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { useAuth as useClerkAuth, useUser } from '@clerk/clerk-react';
import { clearAppMeBootstrap } from 'utils/auth/loadAppUserProfile';

type AuthValue = {
  isInitialized: boolean;
  isLoggedIn: boolean;
  user: any | null;
  token: string | null;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
  login: (...args: any[]) => Promise<any>;
  register: (...args: any[]) => Promise<any>;
  forgotPassword: (...args: any[]) => Promise<any>;
  resetPassword: (...args: any[]) => Promise<any>;
  awsResetPassword: (...args: any[]) => Promise<any>;
  firebaseRegister: (...args: any[]) => Promise<any>;
  firebaseEmailPasswordSignIn: (...args: any[]) => Promise<any>;
  loginAuth: (...args: any[]) => Promise<any>;
  [key: string]: any;
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const clerkAuth = useClerkAuth();
  const { user, isLoaded: userLoaded } = useUser();

  const isInitialized = clerkAuth.isLoaded && userLoaded;
  const isLoggedIn = !!clerkAuth.isSignedIn;

  const value = useMemo<AuthValue>(
    () => ({
      isInitialized,
      isLoggedIn,
      user,
      token: null,
      logout: async () => {
        console.log('[DEBUG-REFRESH] logout() called', new Date().toISOString(), new Error().stack);
        localStorage.removeItem('companyId');
        localStorage.removeItem('whatsapp-token');
        clearAppMeBootstrap();
        await unregisterNativePush();
        await clerkAuth.signOut({ redirectUrl: '/login' });
      },
      getToken: () => clerkAuth.getToken(),
      login: async () => null,
      register: async () => null,
      forgotPassword: async () => null,
      resetPassword: async () => null,
      awsResetPassword: async () => null,
      firebaseRegister: async () => null,
      firebaseEmailPasswordSignIn: async () => null,
      loginAuth: async () => null,
    }),
    [isInitialized, isLoggedIn, user, clerkAuth],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('context must be use inside provider');
  return context;
}
